/**
 * Gemini APIサービス
 *
 * Google Gemini 2.5 Flash Liteを使用したAI機能を提供
 */

// Gemini APIエンドポイント
const GEMINI_API_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent';

// APIキーを環境変数から取得
const getApiKey = () => import.meta.env.VITE_GEMINI_API_KEY;

/**
 * Gemini APIエラークラス
 */
class GeminiApiError extends Error {
  constructor(message, statusCode = null) {
    super(message);
    this.name = 'GeminiApiError';
    this.statusCode = statusCode;
  }
}

/**
 * Geminiレスポンスからマークダウンコードブロックを除去
 * @param {string} response - Geminiのレスポンス
 * @returns {string} クリーンなJSON文字列
 */
function cleanJsonResponse(response) {
  // ```json ... ``` または ``` ... ``` を除去
  let cleaned = response.trim();
  cleaned = cleaned.replace(/^```json\s*/i, '');
  cleaned = cleaned.replace(/^```\s*/i, '');
  cleaned = cleaned.replace(/\s*```$/i, '');
  return cleaned.trim();
}

// タグの選択肢（日本語・英語対応）
// 日本語コンテンツには日本語タグ、英語コンテンツには英語タグを使用
export const TAG_OPTIONS_JA = [
  '仕事',
  '趣味・エンタメ',
  'グルメ・レストラン',
  '旅行・観光',
  '学習・教育',
  'ニュース',
  'ショッピング',
  '健康・医療',
  '技術・IT',
  'その他'
];

export const TAG_OPTIONS_EN = [
  'Work',
  'Hobbies & Entertainment',
  'Food & Dining',
  'Travel & Tourism',
  'Learning & Education',
  'News',
  'Shopping',
  'Health & Medical',
  'Technology & IT',
  'Other'
];

// 後方互換性のため日本語タグをデフォルトとしてエクスポート
export const TAG_OPTIONS = TAG_OPTIONS_JA;

/**
 * Gemini APIを呼び出す
 * @param {string} prompt - プロンプト
 * @param {Object} options - オプション
 * @returns {Promise<string>} 生成されたテキスト
 */
async function callGeminiApi(prompt, options = {}) {
  const apiKey = getApiKey();

  console.log('[KnoVelo Gemini] API呼び出し開始');
  console.log('[KnoVelo Gemini] APIキー設定:', apiKey ? `設定済み (${apiKey.substring(0, 10)}...)` : '未設定');

  if (!apiKey) {
    console.error('[KnoVelo Gemini] APIキーが設定されていません。.envファイルを確認してください。');
    throw new Error('Gemini APIキーが設定されていません');
  }

  const generationConfig = {
    temperature: options.temperature || 0.3,
    maxOutputTokens: options.maxOutputTokens || 200,
    topP: options.topP || 0.8,
    topK: options.topK || 40
  };

  console.log('[KnoVelo Gemini] プロンプト長:', prompt.length, '文字');

  try {
    const requestBody = {
      contents: [{
        parts: [{ text: prompt }]
      }],
      generationConfig
    };

    console.log('[KnoVelo Gemini] リクエスト送信中...');

    const response = await fetch(`${GEMINI_API_ENDPOINT}?key=${apiKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    console.log('[KnoVelo Gemini] レスポンスステータス:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[KnoVelo Gemini] APIエラーレスポンス:', errorText);
      console.error('[KnoVelo Gemini] HTTPステータス:', response.status);

      // ステータスコード付きのエラーを投げる
      throw new GeminiApiError(
        `Gemini APIエラー: ${response.status}`,
        response.status
      );
    }

    const data = await response.json();
    console.log('[KnoVelo Gemini] レスポンスデータ:', JSON.stringify(data).substring(0, 500));

    const text = data.candidates?.[0]?.content?.parts?.[0]?.text || '';

    if (!text) {
      console.warn('[KnoVelo Gemini] 空のレスポンス。candidates:', data.candidates);
    }

    console.log('[KnoVelo Gemini] API呼び出し成功。レスポンス長:', text.length, '文字');
    return text;

  } catch (error) {
    console.error('[KnoVelo Gemini] API呼び出しエラー:', error.message);
    console.error('[KnoVelo Gemini] エラー詳細:', error);
    throw error;
  }
}

/**
 * テキストの要約を生成（多言語対応）
 * コンテンツの言語を自動検出し、同じ言語で要約を生成
 * @param {string} content - 要約対象のテキスト
 * @returns {Promise<string>} 要約テキスト
 */
export async function generateSummary(content) {
  if (!content || content.trim().length === 0) {
    return '';
  }

  // テキストを3000文字に制限
  const truncatedContent = content.substring(0, 3000);

  // 多言語対応プロンプト：コンテンツの言語を検出し、同じ言語で要約
  const prompt = `Analyze the following content and generate a summary in the SAME LANGUAGE as the content.
- If the content is in Japanese, respond in Japanese (30文字以内)
- If the content is in English, respond in English (within 50 characters)
- If the content is in another language, respond in that language

Be concise and capture the main point clearly.
Output ONLY the summary text, nothing else.

Content:
${truncatedContent}`;

  try {
    const summary = await callGeminiApi(prompt, {
      maxOutputTokens: 1000,
      temperature: 0.3
    });

    return summary.trim().replace(/\n/g, ' ');
  } catch (error) {
    console.error('[KnoVelo Gemini] 要約生成エラー:', error);
    return '';
  }
}

/**
 * テキストに適切なタグを生成（多言語対応）
 * コンテンツの言語を自動検出し、同じ言語でタグを生成
 * @param {string} content - タグ生成対象のテキスト
 * @returns {Promise<string[]>} タグ配列
 */
export async function generateTags(content) {
  if (!content || content.trim().length === 0) {
    return ['その他'];
  }

  const truncatedContent = content.substring(0, 3000);

  // 多言語対応プロンプト：コンテンツの言語に応じたタグを選択
  const prompt = `Analyze the following content and select up to 3 appropriate tags.
First, detect the language of the content, then choose tags from the corresponding list.

If content is in JAPANESE, choose from:
${TAG_OPTIONS_JA.map(tag => `- ${tag}`).join('\n')}

If content is in ENGLISH or other languages, choose from:
${TAG_OPTIONS_EN.map(tag => `- ${tag}`).join('\n')}

Content:
${truncatedContent}

IMPORTANT: Output ONLY a pure JSON array. No markdown, no explanation.
Example for Japanese content: ["仕事", "技術・IT", "学習・教育"]
Example for English content: ["Work", "Technology & IT", "Learning & Education"]`;

  try {
    const response = await callGeminiApi(prompt, {
      maxOutputTokens: 1000,
      temperature: 0.2
    });

    // マークダウンコードブロックを除去してパース
    const cleaned = cleanJsonResponse(response);
    console.log('[KnoVelo Gemini] タグ生成レスポンス（クリーン後）:', cleaned);

    // JSON配列を抽出してパース
    const jsonMatch = cleaned.match(/\[.*?\]/s);
    if (jsonMatch) {
      const tags = JSON.parse(jsonMatch[0]);
      // 日本語タグまたは英語タグのいずれかに含まれるかチェック
      const allValidTags = [...TAG_OPTIONS_JA, ...TAG_OPTIONS_EN];
      const validTags = tags.filter(tag => allValidTags.includes(tag));
      return validTags.length > 0 ? validTags : ['その他'];
    }

    return ['その他'];
  } catch (error) {
    console.error('[KnoVelo Gemini] タグ生成エラー:', error);
    return ['その他'];
  }
}

/**
 * ユーザーの意図を判定
 * @param {string} message - ユーザーのメッセージ
 * @returns {Promise<Object>} 意図判定結果
 */
export async function detectIntent(message) {
  console.log('[KnoVelo Gemini] 意図判定開始:', message);

  const prompt = `あなたはKnoVeloというページ保存サービスのAIアシスタント「Thoth」です。
ユーザーのメッセージを分析し、意図を判定してください。

ユーザーのメッセージ: "${message}"

以下のJSON形式で回答してください:
{
  "intent": "greeting" | "search" | "conversation" | "contact",
  "response": "ユーザーへの応答（searchの場合は空文字）",
  "searchQuery": "検索クエリ（searchの場合のみ）"
}

【重要】判定基準（上から順に優先）:

1. "greeting" - 挨拶の場合のみ:
   - 「こんにちは」「おはよう」「こんばんは」「やあ」「ハロー」「hello」「hi」など
   - responseに「こんにちは！保存したページを探すお手伝いをします。何をお探しですか？」

2. "contact" - 問い合わせ・連絡したい場合:
   - 「問い合わせ」「連絡」「サポート」「意見」「要望」「フィードバック」「バグ報告」など
   - responseは空文字（ChatBotで特別処理するため）

3. "conversation" - 以下の場合のみ:
   - KnoVeloの使い方や機能についての質問（「KnoVeloとは」「使い方」など）
   - エラーやトラブルについての質問（「エラー」「動かない」「保存できない」「認証」など）
   - responseに適切な回答を返す

4. "search" - 上記以外はすべてsearch:
   - ページ検索（「〇〇のページ」「〇〇を探して」「〇〇の記事」など）
   - 特定のキーワードやトピック（「claude code」「AI」「プログラミング」など）
   - 翻訳・言語関連のリクエスト（「英語にして」「翻訳」など）→ search
   - 具体的な名前やタイトル → search
   - responseは空文字、searchQueryにユーザーのメッセージをそのまま入れる

【例】
- 「こんにちは」→ greeting
- 「問い合わせしたい」→ contact
- 「サポートに連絡」→ contact
- 「フィードバックを送りたい」→ contact
- 「KnoVeloって何？」→ conversation
- 「エラーが出た」→ conversation
- 「保存できない」→ conversation
- 「認証トークンがありません」→ conversation
- 「使い方を教えて」→ conversation
- 「claude codeの記事」→ search
- 「英語にして」→ search
- 「デザイン」→ search
- 「昨日見たページ」→ search
- 「赤い橋」→ search
- 「AIの記事」→ search
- 「プログラミング」→ search

重要: 純粋なJSONのみを出力。マークダウン不要。`;

  try {
    const response = await callGeminiApi(prompt, {
      maxOutputTokens: 2000,
      temperature: 0.3
    });

    console.log('[KnoVelo Gemini] 意図判定レスポンス:', response);

    // マークダウンコードブロックを除去
    const cleaned = cleanJsonResponse(response);

    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      console.log('[KnoVelo Gemini] 意図判定結果:', parsed);
      return parsed;
    }

    console.warn('[KnoVelo Gemini] JSONパース失敗。レスポンス:', response);
    // パース失敗時はconversationとして扱う
    return {
      intent: 'conversation',
      response: 'すみません、よく分かりませんでした。もう少し詳しく教えていただけますか？',
      searchQuery: ''
    };
  } catch (error) {
    console.error('[KnoVelo Gemini] 意図判定エラー:', error.message);
    console.error('[KnoVelo Gemini] エラースタック:', error.stack);

    // 503エラーの場合は特別なレスポンスを返す
    if (error.statusCode === 503 || error.statusCode === 429) {
      return {
        intent: 'error',
        response: 'サーバーが混雑しています。しばらく待ってから再度お試しください。',
        searchQuery: '',
        errorType: 'server_busy'
      };
    }

    return {
      intent: 'error',
      response: '申し訳ありません、一時的に応答できません。もう一度お試しください。',
      searchQuery: '',
      errorType: 'api_error'
    };
  }
}

/**
 * 会話応答を生成（ページ情報を含む）
 * @param {string} message - ユーザーのメッセージ
 * @param {number} pageCount - 保存ページ数
 * @returns {Promise<string>} 応答テキスト
 */
export async function generateConversation(message, pageCount = 0) {
  const pageInfo = pageCount > 0
    ? `ユーザーは現在${pageCount}件のページを保存しています。`
    : 'ユーザーはまだページを保存していません。会話の最後に「ページを保存すると、後から曖昧な記憶でも探せるようになりますよ」と自然に案内してください。';

  const prompt = `あなたはKnoVeloというページ保存サービスのAIアシスタント「Thoth（トト）」です。
エジプト神話の知恵の神トトをモチーフにした、親しみやすく知的なキャラクターです。

${pageInfo}

ユーザーのメッセージ: "${message}"

【KnoVeloの機能説明】
- Chrome拡張機能でページを保存
- AIが自動で要約とタグを生成
- 曖昧な記憶でもAI検索で探せる

【トラブルシューティング（エラーについて聞かれたら案内）】
- 「認証トークンがありません」エラー → Webアプリで一度ログアウトして再ログインしてください。IDトークンが更新されます。
- 「保存に失敗しました」エラー → ネットワーク接続を確認し、再ログインをお試しください。
- 拡張機能が動作しない → 1)Webアプリにログイン中か確認 2)拡張機能を再読み込み（chrome://extensions で更新ボタン）
- ログイン状態が同期されない → Webアプリのページを開いた状態で拡張機能を使用してください。
- IDトークンは1時間で期限切れになります。長時間使用後にエラーが出たら再ログインしてください。

以下のルールで応答してください:
- 簡潔に回答する（トラブル対応は詳しく）
- KnoVeloについて聞かれたら機能を説明する
- エラーやトラブルについて聞かれたら、質問で返さず、上記の対処法をすべて案内する
- 絵文字は使わない

【最重要ルール】
- 雑談には対応しない
- 推測、憶測、思い込みで回答を生成しないこと
- 上記のKnoVelo機能説明とトラブルシューティング以外の質問には一切回答しない
- 対応できない質問には「保存されたページの検索と、KnoVeloの使い方・エラー対応のみお答えできます。」と回答する

応答のみを出力してください。`;

  try {
    const response = await callGeminiApi(prompt, {
      maxOutputTokens: 2000,
      temperature: 0.8
    });

    return response.trim();
  } catch (error) {
    console.error('[KnoVelo Gemini] 会話生成エラー:', error);
    return '申し訳ありません、一時的に応答できません。';
  }
}

/**
 * 曖昧検索を実行
 * @param {string} query - 検索クエリ
 * @param {Object[]} pages - 検索対象のページ配列
 * @returns {Promise<Object>} 検索結果
 */
export async function fuzzySearch(query, pages) {
  console.log('[KnoVelo Gemini] 曖昧検索開始:', query);
  console.log('[KnoVelo Gemini] 検索対象ページ数:', pages.length);

  if (!query || pages.length === 0) {
    console.log('[KnoVelo Gemini] 検索対象なし');
    return {
      results: [],
      message: 'まだページが保存されていません。ページを保存してみましょう！',
      errorType: 'no_pages'
    };
  }

  // ページ数を制限（トークン節約）
  const limitedPages = pages.slice(0, 100);
  console.log('[KnoVelo Gemini] 制限後ページ数:', limitedPages.length);

  // ページ情報をフォーマット
  const pagesFormatted = limitedPages.map((page, index) => {
    const createdAt = page.createdAt?.toDate
      ? page.createdAt.toDate()
      : new Date(page.createdAt);

    return `[${index + 1}] ID: ${page.pageId}
タイトル: ${page.title}
要約: ${page.summary || 'なし'}
保存日時: ${formatDate(createdAt)}
曜日: ${page.context?.dayOfWeek || '不明'}
時間帯: ${page.context?.timeOfDay || '不明'}`;
  }).join('\n\n');

  // デバッグ: フォーマット後のページ情報（最初の2件）
  console.log('[KnoVelo Gemini] フォーマット後のページ情報（先頭部分）:');
  console.log(pagesFormatted.substring(0, 1000));

  // 今日の日付情報を追加
  const today = new Date();
  const todayStr = `${today.getFullYear()}/${today.getMonth() + 1}/${today.getDate()}`;

  const prompt = `ユーザーの質問: "${query}"
今日の日付: ${todayStr}

以下は、ユーザーが保存したページのリストです。
ユーザーの質問に合致するページのみを選んでください（最大5つ）。

【重要なルール】
- 「今日」「昨日」「今週」などの時間条件がある場合、その条件に合う日付のページのみを返す
- 条件に合うページが1件なら1件だけ、0件なら空配列を返す
- 条件に合わないページを水増しして返さないこと

保存ページ:
${pagesFormatted}

回答は以下のJSON形式でお願いします:
{
  "results": [
    {"pageId": "...", "relevance": 0.95, "reason": "関連度の理由を日本語で"},
    ...
  ],
  "message": "ユーザーへのメッセージ（日本語、該当なしの場合はその旨を伝える）"
}

【最重要ルール】
- 推測や憶測で結果を生成しないこと
- 条件に完全に合致するページのみを返す
- 該当するページがなければ results は空配列 [] にする
- 「似ているかもしれない」「関連があるかも」という曖昧な理由で含めない
- messageには正直に「該当するページが見つかりませんでした」と伝える

重要: 純粋なJSONのみを出力してください。マークダウンのコードブロック（\`\`\`json や \`\`\`）は絶対に使わないでください。`;

  try {
    console.log('[KnoVelo Gemini] 曖昧検索API呼び出し...');
    const response = await callGeminiApi(prompt, {
      maxOutputTokens: 4000,
      temperature: 0.3
    });

    console.log('[KnoVelo Gemini] 曖昧検索レスポンス:', response.substring(0, 500));

    // マークダウンコードブロックを除去
    const cleaned = cleanJsonResponse(response);
    console.log('[KnoVelo Gemini] クリーン後レスポンス:', cleaned.substring(0, 500));

    // JSON部分を抽出
    const jsonMatch = cleaned.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      console.log('[KnoVelo Gemini] 抽出したJSON:', jsonMatch[0].substring(0, 500));
      const result = JSON.parse(jsonMatch[0]);
      console.log('[KnoVelo Gemini] パース結果:', result.results?.length, '件');
      console.log('[KnoVelo Gemini] パース結果の詳細:', JSON.stringify(result.results, null, 2));

      // デバッグ: 利用可能なpageIdを出力
      console.log('[KnoVelo Gemini] 利用可能なpageId一覧:', limitedPages.map(p => p.pageId));

      // ページIDをページオブジェクトに変換
      const resultsWithPages = result.results.map(r => {
        console.log('[KnoVelo Gemini] マッチング試行: pageId=', r.pageId);
        const page = limitedPages.find(p => p.pageId === r.pageId);
        console.log('[KnoVelo Gemini] マッチ結果:', page ? 'found' : 'NOT FOUND');
        return {
          ...r,
          page: page || null
        };
      }).filter(r => r.page !== null);

      console.log('[KnoVelo Gemini] 最終結果:', resultsWithPages.length, '件');

      return {
        results: resultsWithPages,
        message: result.message || 'これらのページが見つかりました'
      };
    }

    console.warn('[KnoVelo Gemini] JSONパース失敗。レスポンス:', response);
    return {
      results: [],
      message: '該当するページが見つかりませんでした。別のキーワードで試してみてください。',
      errorType: 'no_results'
    };
  } catch (error) {
    console.error('[KnoVelo Gemini] 検索エラー:', error.message);
    console.error('[KnoVelo Gemini] エラースタック:', error.stack);

    // エラーの種類に応じてメッセージを変更
    if (error.statusCode === 503 || error.statusCode === 429) {
      return {
        results: [],
        message: 'サーバーが混雑しています。しばらく待ってから再度お試しください。',
        errorType: 'server_busy'
      };
    }

    return {
      results: [],
      message: '一時的にエラーが発生しました。しばらくしてからもう一度お試しください。',
      errorType: 'api_error'
    };
  }
}

/**
 * 日付をフォーマット
 * @param {Date} date - 日付
 * @returns {string} フォーマットされた日付
 */
function formatDate(date) {
  if (!date || !(date instanceof Date)) return '不明';
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`;
}
